// IamWorkspaceHost — the small impure host that drives the pure Account /
// WorkspaceMembers / InviteAndGrant surfaces against the LIVE control-plane
// routes (GET /workspaces, GET|POST /workspaces/:id/members, DELETE
// /workspaces/:id/members/:userId, POST /account/delete, POST /auth/logout).
//
// Same posture as SignInHost / SettingsView: a legitimate impure-shell
// composition root (IDD 94442b9d names this split explicitly — the
// control-plane data these three surfaces show is workspace/membership
// rows, never domain material, so it is loaded here rather than threaded
// through the central AppShellModel reducer). Mounted from Settings (the
// existing "gear" affordance) — see settingsView.tsx.
//
// Workspace switcher: `GET /workspaces` returns every workspace the caller
// can reach, owned + shared. The live default is one (owned) — Account
// itself collapses that to a plain label; a second entry appears the moment
// someone invites this account into their workspace, which is real and
// testable today, not speculative.
//
// Demonstrator scope (FR-1.3, DemoScope.reachableWorkspaceRoles): only
// owned workspaces are listed, and the member list + invite dialog are not
// rendered (nor is the member list fetched) while "member" is out of
// scope. All of it stays wired; widening that constant brings it back.
//
// Delete-account reloads the page afterwards rather than threading a new
// frame-state transition through the reducer — the session cookie changed
// server-side, so a fresh boot is the correct re-gate (the same idiom
// SignInHost's `onContinue` already uses). Sign-out is the shell's
// (`onSignOut`, one place for the menu and Settings).

export type { IamManagementPorts, IamWorkspaceHostProps };
export { IamWorkspaceHost };

import * as DemoScope from "@bilda/app/demoScope";
import { Result } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import { useEffect, useState } from "react";
import { localErrorId } from "../../app/errorId.js";
import type {
    IamPorts,
    WorkspaceMember,
    WorkspaceSummary,
} from "../../app/ports/iam.js";
import { Button } from "../Button/Button.js";
import { Dialog } from "../Dialog/Dialog.js";
import { ErrorBanner } from "../ErrorBanner/ErrorBanner.js";
import { Spinner } from "../Spinner/Spinner.js";
import { Account } from "./Account/Account.js";
import {
    InviteAndGrant,
    type InviteAndGrantDraft,
} from "./InviteAndGrant/InviteAndGrant.js";
import { WorkspaceMembers } from "./WorkspaceMembers/WorkspaceMembers.js";
import type { AccountWorkspace } from "./WorkspaceSwitcher/WorkspaceSwitcher.js";
import "./IamWorkspaceHost.css";

// The slice of IamPorts this host actually calls — narrower than the full
// port bundle so the caller (Centre.tsx) can pass `ports` straight through.
type IamManagementPorts = Pick<
    IamPorts,
    | "listWorkspaces"
    | "listWorkspaceMembers"
    | "inviteWorkspaceMember"
    | "removeWorkspaceMember"
    | "deleteAccount"
>;

// Workspace sharing is visible only when a person can reach a workspace
// as a member (hidden in the demonstrator).
const sharingVisible = DemoScope.isReachableWorkspaceRole("member");

type IamWorkspaceHostProps = {
    readonly userId: string;
    readonly email: string;
    readonly ports: IamManagementPorts;
    // Sign out is the shell's (server sign-out, then a fresh boot).
    readonly onSignOut: () => void;
};

type WorkspacesState =
    | { readonly type: "loading" }
    | {
          readonly type: "loaded";
          readonly workspaces: readonly WorkspaceSummary[];
      }
    | { readonly type: "error" };

type MembersState =
    | { readonly type: "loading" }
    | { readonly type: "loaded"; readonly members: readonly WorkspaceMember[] }
    | { readonly type: "error" };

type InviteState =
    | { readonly type: "closed" }
    | {
          readonly type: "open";
          readonly email: string;
          readonly submitting: boolean;
      };

const IamWorkspaceHost = ({
    userId,
    email,
    ports,
    onSignOut,
}: IamWorkspaceHostProps) => {
    const { t } = useLingui();

    const [workspacesState, setWorkspacesState] = useState<WorkspacesState>({
        type: "loading",
    });
    const [activeWorkspaceId, setActiveWorkspaceId] = useState<
        string | undefined
    >(undefined);
    const [membersState, setMembersState] = useState<MembersState>({
        type: "loading",
    });
    const [confirmingRemoveUserId, setConfirmingRemoveUserId] = useState<
        string | undefined
    >(undefined);
    const [confirmingDeleteAccount, setConfirmingDeleteAccount] =
        useState(false);
    const [inviteState, setInviteState] = useState<InviteState>({
        type: "closed",
    });

    // Load every reachable workspace once, then pick the owned one as the
    // default active workspace (falling back to the first entry — a member
    // with no owned workspace of their own still lands somewhere real).
    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            const r = await ports.listWorkspaces();
            if (cancelled) return;
            if (Result.isErr(r)) {
                setWorkspacesState({ type: "error" });
                return;
            }
            const reachable = r.value.filter((w) =>
                DemoScope.isReachableWorkspaceRole(w.role),
            );
            setWorkspacesState({ type: "loaded", workspaces: reachable });
            if (reachable.length === 0) {
                setMembersState({ type: "loaded", members: [] });
                return;
            }
            setActiveWorkspaceId(
                (prev) =>
                    prev ??
                    (reachable.find((w) => w.role === "owner") ?? reachable[0])
                        .id,
            );
        };
        void run();
        return () => {
            cancelled = true;
        };
    }, [ports]);

    // Reload the member list whenever the active workspace changes.
    useEffect(() => {
        if (activeWorkspaceId === undefined || !sharingVisible) return;
        let cancelled = false;
        setMembersState({ type: "loading" });
        const run = async () => {
            const r = await ports.listWorkspaceMembers(activeWorkspaceId);
            if (cancelled) return;
            if (Result.isErr(r)) {
                setMembersState({ type: "error" });
                return;
            }
            setMembersState({ type: "loaded", members: r.value });
        };
        void run();
        return () => {
            cancelled = true;
        };
    }, [ports, activeWorkspaceId]);

    const onConfirmDeleteAccount = () => {
        void ports.deleteAccount().then(() => window.location.reload());
    };

    const onSubmitInvite = (inviteEmail: string) => {
        if (activeWorkspaceId === undefined) return;
        setInviteState((prev) =>
            prev.type === "open" ? { ...prev, submitting: true } : prev,
        );
        void ports
            .inviteWorkspaceMember(activeWorkspaceId, inviteEmail)
            .then((r) => {
                if (Result.isErr(r)) {
                    setInviteState((prev) =>
                        prev.type === "open"
                            ? { ...prev, submitting: false }
                            : prev,
                    );
                    return;
                }
                setInviteState({ type: "closed" });
                const invited = r.value;
                setMembersState((prev) =>
                    prev.type === "loaded"
                        ? {
                              type: "loaded",
                              members: [...prev.members, invited],
                          }
                        : prev,
                );
            });
    };

    const onConfirmRemove = (removeUserId: string) => {
        if (activeWorkspaceId === undefined) return;
        void ports
            .removeWorkspaceMember(activeWorkspaceId, removeUserId)
            .then((r) => {
                setConfirmingRemoveUserId(undefined);
                if (Result.isErr(r)) return;
                setMembersState((prev) =>
                    prev.type === "loaded"
                        ? {
                              type: "loaded",
                              members: prev.members.filter(
                                  (m) => m.userId !== removeUserId,
                              ),
                          }
                        : prev,
                );
            });
    };

    if (workspacesState.type === "loading") {
        return (
            <div className="bilda-iam-workspace-host__loading">
                <Spinner label={t`Loading your workspace…`} />
            </div>
        );
    }
    if (workspacesState.type === "error") {
        return (
            <ErrorBanner
                severity="danger"
                title={t`Couldn't load your workspace`}
                body={t`Try reopening this section.`}
                kept={t`Nothing has been lost — only this view is affected.`}
                errorId={localErrorId("workspace-load-failed")}
            />
        );
    }

    const workspaces = workspacesState.workspaces;
    const active = workspaces.find((w) => w.id === activeWorkspaceId);
    // The real workspace name (F56) is the denormalized `name` read-copy
    // `GET /workspaces` now returns. When it is "" (unnamed), fall back to a
    // plain role-derived label — never invented data. (Distinct SHARED
    // workspaces still read identically only while both are unnamed.)
    const accountWorkspaces: readonly AccountWorkspace[] = workspaces.map(
        (w) => ({
            id: w.id,
            role: w.role,
            label:
                w.name !== ""
                    ? w.name
                    : w.role === "owner"
                      ? t`Your workspace`
                      : t`Shared workspace`,
        }),
    );
    const memberRows =
        membersState.type === "loaded"
            ? membersState.members.map((m) => ({
                  ...m,
                  isYou: m.userId === userId,
                  canRemove:
                      active?.role === "owner" &&
                      m.role !== "owner" &&
                      m.userId !== userId,
              }))
            : [];
    const inviteDraft: InviteAndGrantDraft = {
        email: inviteState.type === "open" ? inviteState.email : "",
    };

    return (
        <div className="bilda-iam-workspace-host">
            <Account
                email={email}
                workspaces={accountWorkspaces}
                activeWorkspaceId={activeWorkspaceId ?? ""}
                onSwitchWorkspace={setActiveWorkspaceId}
                onSignOut={onSignOut}
                onRequestDeleteAccount={() => setConfirmingDeleteAccount(true)}
            />

            {!sharingVisible ? null : membersState.type === "loading" ? (
                <div className="bilda-iam-workspace-host__loading">
                    <Spinner label={t`Loading members…`} />
                </div>
            ) : membersState.type === "error" ? (
                <ErrorBanner
                    severity="danger"
                    title={t`Couldn't load members`}
                    body={t`Try reopening this section.`}
                    kept={t`Nothing has been lost — only this view is affected.`}
                    errorId={localErrorId("members-load-failed")}
                />
            ) : (
                <WorkspaceMembers
                    members={memberRows}
                    confirmingRemoveUserId={confirmingRemoveUserId}
                    onInvite={
                        active?.role === "owner"
                            ? () =>
                                  setInviteState({
                                      type: "open",
                                      email: "",
                                      submitting: false,
                                  })
                            : undefined
                    }
                    onRequestRemove={setConfirmingRemoveUserId}
                    onConfirmRemove={onConfirmRemove}
                    onCancelRemove={() => setConfirmingRemoveUserId(undefined)}
                />
            )}

            <InviteAndGrant
                open={sharingVisible && inviteState.type === "open"}
                onOpenChange={(open) => {
                    if (!open) setInviteState({ type: "closed" });
                }}
                draft={inviteDraft}
                onDraftChange={(draft) =>
                    setInviteState((prev) =>
                        prev.type === "open"
                            ? { ...prev, email: draft.email }
                            : prev,
                    )
                }
                onSubmit={onSubmitInvite}
                submitting={
                    inviteState.type === "open" ? inviteState.submitting : false
                }
            />

            <Dialog
                open={confirmingDeleteAccount}
                onOpenChange={(open) => {
                    if (!open) setConfirmingDeleteAccount(false);
                }}
                title={t`Delete account?`}
                description={t`Your account and your workspace are deleted permanently. This can't be undone.`}
                footer={
                    <>
                        <Button
                            variant="ghost"
                            onClick={() => setConfirmingDeleteAccount(false)}
                        >
                            {t`Cancel`}
                        </Button>
                        <Button
                            variant="danger"
                            onClick={onConfirmDeleteAccount}
                        >
                            {t`Delete account`}
                        </Button>
                    </>
                }
            />
        </div>
    );
};
